import React, { useState } from "react";
import { Box, Chip, Dialog, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon } from "../icons";
import { ArrowBackIos as ArrowBackIosIcon } from "../icons";
import { ArrowForwardIos as ArrowForwardIosIcon } from "../icons";
import { PhotoLibrary as PhotoLibraryIcon } from "../icons";
import { ImageNotSupported as ImageNotSupportedIcon } from "../icons";

// Galería de un producto de tienda: fotos + video opcional, con visor a
// pantalla completa (mismo patrón que viajes/ver.js y autos/ver.js:
// contador "1 / N", flechas, fondo negro).
const ProductMediaGallery = ({ images, videoUrl, alt = "" }) => {
  const items = [
    ...(Array.isArray(images) ? images.filter(Boolean) : []).map((src) => ({ type: "image", src })),
    ...(videoUrl ? [{ type: "video", src: videoUrl }] : []),
  ];

  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const hasMultiple = items.length > 1;
  const current = items[index];

  const goTo = (newIndex) => setIndex((newIndex + items.length) % items.length);

  return (
    <Box>
      <Box
        onClick={() => items.length > 0 && setFullscreen(true)}
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 3",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "action.hover",
          cursor: items.length > 0 ? "pointer" : "default",
        }}
      >
        {items.length === 0 ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "text.disabled",
            }}
          >
            <ImageNotSupportedIcon sx={{ fontSize: 36 }} />
            <Typography sx={{ fontSize: 12 }}>Sin imágenes</Typography>
          </Box>
        ) : current.type === "video" ? (
          <Box
            component="video"
            src={current.src}
            controls
            onClick={(e) => e.stopPropagation()}
            sx={{ width: "100%", height: "100%", objectFit: "contain", bgcolor: "#000" }}
          />
        ) : (
          <Box
            component="img"
            src={current.src}
            alt={alt}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}

        {hasMultiple && (
          <Chip
            icon={<PhotoLibraryIcon sx={{ color: "white !important" }} />}
            label="Ver todas"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              bgcolor: "rgba(0,0,0,0.65)",
              color: "white",
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {hasMultiple && (
        <Box sx={{ display: "flex", gap: 0.75, mt: 1, overflowX: "auto", pb: 0.5 }}>
          {items.map((item, i) => (
            <Box
              key={item.src + i}
              onClick={() => goTo(i)}
              sx={{
                flexShrink: 0,
                width: 56,
                height: 56,
                borderRadius: 1.5,
                overflow: "hidden",
                cursor: "pointer",
                border: i === index ? "2px solid" : "2px solid transparent",
                borderColor: i === index ? "primary.main" : "transparent",
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {item.type === "video" ? (
                <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Video</Typography>
              ) : (
                <Box component="img" src={item.src} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#000" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 1,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "rgba(0,0,0,0.6)",
          }}
        >
          <IconButton onClick={() => setFullscreen(false)} sx={{ color: "white" }} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
          {hasMultiple && (
            <Typography sx={{ color: "white", fontSize: 14 }}>
              {index + 1} / {items.length}
            </Typography>
          )}
          <Box sx={{ width: 40 }} />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            minHeight: 0,
            p: 2,
          }}
        >
          {current && (
            <>
              {current.type === "video" ? (
                <Box
                  component="video"
                  src={current.src}
                  controls
                  autoPlay
                  sx={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 2 }}
                />
              ) : (
                <Box
                  component="img"
                  src={current.src}
                  alt={alt}
                  sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 2 }}
                />
              )}
              {hasMultiple && (
                <>
                  <IconButton
                    onClick={() => goTo(index - 1)}
                    sx={{ position: "absolute", left: 8, color: "white", bgcolor: "rgba(0,0,0,0.4)" }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => goTo(index + 1)}
                    sx={{ position: "absolute", right: 8, color: "white", bgcolor: "rgba(0,0,0,0.4)" }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </>
              )}
            </>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ProductMediaGallery;
